import {
    Component, ElementRef, EventEmitter, Input, Output,
    ViewChild,
} from '@angular/core';
import { SnackbarService } from '../../../services/snackbar.service';
import { NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'file-selector',
    templateUrl: './file-selector.component.html',
    styleUrl: './file-selector.component.scss',
    imports: [NgIf, MatIcon],
})
export class FileSelectorComponent {
    /**
     * Reference to fileUpload element that can be clicked with fileUpload?.nativeElement.click() to open the system's file explorer
     */
    @ViewChild('fileUpload') fileUpload?: ElementRef<HTMLInputElement>;

    /**
     * Optional input to specify allowed file extensions in drag/drop or browse.
     * Value is an array of strings, e.g. `['.jpg', '.png']`
     */
    @Input() allowedFileExtensions?: string[];

    /**
     * Set to true to allow selecting multiple files
     */
    @Input() multiple = false;

    /**
     * Maximum amount of files to upload at the same time
     */
    @Input() maxFiles = 9999;

    /**
     * Default status for newly added documents
     */
    @Input() defaultStatus: UploadedFileType['status'] = 'queued';

    /**
     * Reference to selected files
     * Can be set via input if the file-selector is used for editing
     */
    @Input() files: UploadedFileType[] = [];

    /**
     * Image that is displayed on the component where the user clicks or drags to select images.
     */
    @Input() svgIcon = 'file-plus-2';

    /**
     * True if upload box should have red border to indicate invalid state
     */
    @Input() invalid = false;

    /**
     * Enable drag/dropping files into the box. Also changes the instruction text.
     */
    @Input() dragDropEnabled = true;

    /**
     * Set to false to hide subtext
     */
    @Input() showSubtext = true;

    /**
     * True if the default file-selector template should be used
     * False if the parent component provides its own content
     */
    @Input() useDefaultTemplate = true;

    /**
     * Max file size per file allowed to select
     */
    @Input() maxFileSize = 25000000;

    /**
     * Emits file array when a new file has been selected
     */
    @Output() filesSelected = new EventEmitter<{
        file: File; id?: string; status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry'; }[]>();

    /**
     * Is user hovering with (a) file(s) above the drag/drop container
     */
    isHovered = false;

    /**
     * Container that was entered when drag/dropping. Used to avoid when drag/drop container has child elements
     * that incorrectly set the isHovered states to false.
     */
    enterTarget: EventTarget | null = null;

    constructor(private snackbarService: SnackbarService) {}

    /**
     * Checks an array of files to determine if any file has an invalid extension
     * @param files File array
     * @returns True if all files have an valid extension. False otherwise
     */
    verifyFileExtensions(files: File[]): boolean {
        // If no list of allowed file extensions is defined, always return true
        if (!this.allowedFileExtensions?.length) {
            return true;
        }
        return files.every((file) => {
            // Regex match file extension
            const extension = /\.\w+$/.exec(file.name);
            // Return true if match is found and part of allowedFileExtension
            return extension && this.allowedFileExtensions?.includes(extension[0].toLowerCase());
        });
    }

    /**
     * Handle drag over events
     * @param event Event
     */
    onDragOver(event: Event): void {
        event.preventDefault();
    }

    /**
     * Handle drag enter events
     * @param event Event
     */
    onDragEnter(event: Event): void {
        this.enterTarget = event.target;
        event.preventDefault();
        event.stopPropagation();
        this.isHovered = true;
    }

    /**
     * Handle drag leave events
     * @param event Event
     */
    onDragLeave(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        // Only set isHovered to false if user leaves drag/drop box they originally entered
        if (this.enterTarget === event.target) {
            this.isHovered = false;
        }
    }

    /**
     * Handle drop events
     * @param event Event
     */
    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer?.files.length) {
            const files = Array.from(event.dataTransfer.files);
            // Check if all selected files are of allowed extension
            if (!this.verifyFileExtensions(files)) {
                this.snackbarService.error($localize`Een of meerdere bestanden hebben een formaat dat niet toegestaan is`);
            // Check if all selected files are below max file size
            } else if (files.some((file) => file.size > this.maxFileSize)) {
                this.snackbarService.error($localize`Een of meerdere bestanden zijn te groot`);
            } else {
                this.files = this.files.concat(Array.from(event.dataTransfer.files).map((file) => ({ file, status: this.defaultStatus })));
                this.filesSelected.next(this.files);
            }
        }
        this.isHovered = false;
    }

    /**
     * Handle file selection via Select Files browser dialog
     * @param evt Event
     */
    handleFilesSelected(target: HTMLInputElement): void {
        if (target.files?.length) {
            const files = Array.from(target.files);
            // Check if all selected files are of allowed extension
            if (!this.verifyFileExtensions(files)) {
                this.snackbarService.error($localize`Een of meerdere bestanden hebben een formaat dat niet toegestaan is`);
            // Check if all selected files are below max file size
            } else if (files.some((file) => file.size > this.maxFileSize)) {
                this.snackbarService.error($localize`Een of meerdere bestanden zijn te groot`);
            } else {
                this.files = this.files.concat(Array.from(target.files).map((file) => ({ file, status: this.defaultStatus })));
                if (this.files.length > this.maxFiles) {
                    this.snackbarService.error($localize`Upload maximaal ${this.maxFiles} bestanden tegelijk`);
                    this.files = this.files.splice(0, 5);
                }
                this.filesSelected.next(this.files);
                target.value = '';
            }
        }
    }

    /**
     * Remove a file from the local files array
     * @param index Index of file to remove
     */
    deleteFileAtIndex(index: number): void {
        this.files.splice(index, 1);
    }
}

export interface UploadedFileType {
    file: File;
    id?: string;
    status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry';
    subText?: string;
    created_at?: Date;
    attachment_id?: string;
}
